import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  FOOTBALL_ONBOARDING_SELECT_COMPETITION,
  FOOTBALL_ONBOARDING_TUTORIAL_RECRUIT,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';

import { GoToProWarningDialog } from '@sorare/gameplay/src/components/onboarding/GoToProWarningDialog';

import { ChooseGameMode } from './ChooseGameMode';
import type { UnlockPro_currentUser } from './__generated__/index.graphql';

type Props = {
  currentUser: UnlockPro_currentUser;
  goProMode?: boolean;
};

export const UnlockPro = ({ currentUser, goProMode }: Props) => {
  const navigate = useNavigate();
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const { currentUser: contextCurrentUser } = useCurrentUserContext();
  const limitedCardsCount = contextCurrentUser?.cardCounts.limited || 0;

  const [searchParams, setSearchParams] = useSearchParams();
  const onGoPro = async () => {
    if (limitedCardsCount > 0 && goProMode) {
      setShowWarningDialog(true);
      return;
    }
    // ensure back navigation from market guidance relocks pro
    searchParams.set('relock', 'true');
    setSearchParams(searchParams, {
      replace: true,
    });
    navigate(
      `${FOOTBALL_ONBOARDING_TUTORIAL_RECRUIT}?from=${goProMode ? 'go-pro' : 'onboarding'}`
    );
  };

  return (
    <>
      <ChooseGameMode
        conversionCredit={currentUser.onboardingConversionCredit}
        goProMode={goProMode}
        onGoPro={onGoPro}
        onChooseCommon={() => {
          navigate(FOOTBALL_ONBOARDING_SELECT_COMPETITION);
        }}
      />
      <GoToProWarningDialog
        open={showWarningDialog}
        onClose={() => {
          setShowWarningDialog(false);
        }}
      />
    </>
  );
};

UnlockPro.fragments = {
  currentUser: gql`
    fragment UnlockPro_currentUser on CurrentUser {
      slug
      onboardingConversionCredit(sport: FOOTBALL) {
        id
        percentageDiscount
        ...ChooseGameMode_conversionCredit
      }
    }
    ${ChooseGameMode.fragments.conversionCredit}
  ` as TypedDocumentNode<UnlockPro_currentUser>,
};
